/**
 * MFA Enrollment Mixin
 *
 * Provides reusable methods for MFA enrollment that can be shared across components
 * like DetailsAccount (settings page) and AcceptAccount (sign-up flow).
 */
export default {
  data() {
    return {
      mfaEnrolling: false,
      mfaVerificationId: null,
      mfaPhoneNumber: null,
      mfaPassword: null, // Stored temporarily for resend on mobile
      mfaErrorMessage: '',
    };
  },
  methods: {
    /**
     * Start MFA enrollment process
     * Requires user to have a phone number and verified email
     * @param {Object} options - Enrollment options
     * @param {string} options.phoneNumber - Phone number in E.164 format
     * @param {boolean} options.skipEmailCheck - Skip email verification check (default: false)
     * @param {boolean} options.skipPasswordReauth - Skip password re-authentication (default: false)
     * @returns {Promise<void>}
     */
    async startMFAEnrollment({
      phoneNumber = null,
      skipEmailCheck = false,
      skipPasswordReauth = false,
    } = {}) {
      // Use provided phone number or get from userInfo
      const phone = phoneNumber || (this.userInfo && this.userInfo.phoneNumber);

      if (!phone) {
        this.$modal.show('alert', {
          title: 'Phone Number Required',
          message:
            'You must have a phone number associated with your account to enable two-factor authentication.',
          buttons: {
            closeTitle: 'Ok',
          },
          type: 'warning',
        });
        return;
      }

      // Check if email is verified (unless explicitly skipped)
      if (!skipEmailCheck) {
        const emailVerified = await this.$store.dispatch(
          'user/checkEmailVerified'
        );
        if (!emailVerified) {
          this.$modal.show('alert', {
            title: 'Email Verification Required',
            message:
              'You must verify your email address before enabling two-factor authentication.',
            buttons: {
              closeTitle: 'Ok',
            },
            type: 'warning',
          });
          return;
        }
      }

      // If password re-auth is required, show password prompt
      if (!skipPasswordReauth) {
        this.$modal.show('input-prompt', {
          title: 'Confirm Your Password',
          message:
            'For security, please re-enter your password to enable two-factor authentication.',
          placeholder: 'Enter your password',
          inputType: 'password',
          autocomplete: 'current-password',
          confirmText: 'Continue',
          iconName: 'la-shield-alt',
          iconClass: 'is-warning',
          onConfirm: async password => {
            await this.performMFAEnrollment(phone, password);
          },
          onCancel: () => {
            // User cancelled
          },
        });
      } else {
        // Skip password re-auth (e.g., during sign-up when user just created password)
        await this.performMFAEnrollment(phone, null);
      }
    },

    /**
     * Perform the actual MFA enrollment
     * @param {string} phoneNumber - Phone number to enroll
     * @param {string|null} password - Password for re-authentication (null to skip)
     * @returns {Promise<void>}
     */
    async performMFAEnrollment(phoneNumber, password) {
      try {
        // Re-authenticate if password provided
        if (password) {
          await this.$store.dispatch('user/reauthenticateWithPassword', {
            password,
          });
        }

        // Start MFA enrollment
        this.mfaEnrolling = true;

        const result = await this.$store.dispatch('user/enrollMFA', {
          phoneNumber,
          password,
        });

        this.mfaVerificationId = result.verificationId;
        this.mfaPhoneNumber = phoneNumber;
        this.mfaPassword = password; // Store for potential resend

        // Show the MFA code prompt modal
        this.showMFACodePrompt(phoneNumber);
      } catch (error) {
        window.console.error(
          'Error during re-authentication or enrollment:',
          error
        );
        // Clean up on error so user can retry
        this.mfaEnrolling = false;
        this.mfaPassword = null;

        this.$modal.show('alert', {
          title: 'Enrollment Error',
          message:
            error.message ||
            'Failed to start MFA enrollment. Please try again.',
          buttons: {
            closeTitle: 'Ok',
          },
          type: 'warning',
        });
      }
    },

    /**
     * Show MFA code prompt modal
     * @param {string} phoneNumber - Phone number that received the code
     */
    showMFACodePrompt(phoneNumber) {
      this.$modal.show('mfa-code-prompt', {
        title: 'Verify Your Phone',
        phoneNumber: phoneNumber,
        errorMessage: this.mfaErrorMessage,
        onVerify: async code => {
          await this.verifyMFAEnrollment(code);
        },
        onCancel: () => {
          this.cancelMFAEnrollment();
        },
        onResend: async () => {
          await this.resendMFACode();
        },
      });
    },

    /**
     * Resend MFA verification code
     * @returns {Promise<void>}
     */
    async resendMFACode() {
      try {
        // Clear any previous error
        this.mfaErrorMessage = '';

        // Clear old reCAPTCHA

        // Request new verification code
        const result = await this.$store.dispatch('user/enrollMFA', {
          phoneNumber: this.mfaPhoneNumber,
          password: this.mfaPassword,
        });

        this.mfaVerificationId = result.verificationId;

        // Show success message briefly
        const formattedPhone = this.formatPhoneForDisplay(this.mfaPhoneNumber);
        this.$modal.hide('mfa-code-prompt');

        setTimeout(() => {
          this.$modal.show('alert', {
            title: 'Code Resent',
            message: `A new verification code has been sent to ${formattedPhone}.`,
          });

          setTimeout(() => {
            this.$modal.hide('alert');
            this.showMFACodePrompt(this.mfaPhoneNumber);
          }, 1500);
        }, 100);
      } catch (error) {
        window.console.error('Error resending MFA code:', error);

        this.$modal.hide('mfa-code-prompt');
        this.$modal.show('alert', {
          title: 'Error',
          message:
            'Failed to resend verification code. Please try again or restart the enrollment process.',
          buttons: {
            closeTitle: 'Ok',
          },
          type: 'warning',
        });
      }
    },

    /**
     * Verify MFA enrollment with the SMS code
     * @param {string} code - 6-digit verification code
     * @returns {Promise<void>}
     */
    async verifyMFAEnrollment(code) {
      try {
        await this.$store.dispatch('user/verifyMFAEnrollment', {
          verificationId: this.mfaVerificationId,
          code,
          displayName: 'Phone',
        });

        // Hide the code prompt modal
        this.$modal.hide('mfa-code-prompt');

        this.$modal.show('alert', {
          title: 'Success',
          message:
            'Two-factor authentication has been enabled for your account.',
        });

        // Update MFA status if tracking it locally
        if (this.mfaEnabled !== undefined) {
          this.mfaEnabled = true;
        }

        // Clean up
        this.mfaVerificationId = null;
        this.mfaPhoneNumber = null;
        this.mfaPassword = null;
        this.mfaErrorMessage = '';

        // Call optional success callback if provided
        if (this.onMFAEnrollmentSuccess) {
          this.onMFAEnrollmentSuccess();
        }
      } catch (error) {
        window.console.error('Error verifying MFA enrollment:', error);

        // Set error message and re-show the modal with the error
        this.mfaErrorMessage = 'Invalid code. Please try again.';

        // Hide and re-show modal to update error message
        this.$modal.hide('mfa-code-prompt');
        setTimeout(() => {
          this.showMFACodePrompt(this.mfaPhoneNumber);
        }, 100);
      } finally {
        this.mfaEnrolling = false;
      }
    },

    /**
     * Cancel MFA enrollment process
     */
    cancelMFAEnrollment() {
      this.mfaEnrolling = false;
      this.mfaVerificationId = null;
      this.mfaPhoneNumber = null;
      this.mfaPassword = null;
      this.mfaErrorMessage = '';

      // Call optional cancel callback if provided
      if (this.onMFAEnrollmentCancel) {
        this.onMFAEnrollmentCancel();
      }
    },

    /**
     * Format phone number for display
     * @param {string} phone - Phone number in E.164 format
     * @returns {string} Formatted phone number
     */
    formatPhoneForDisplay(phone) {
      const regex = /(\+)(\d{1})(\d{3})(\d{3})(\d{4})/g;
      return (phone && phone.replace(regex, '($3) $4-$5')) || phone || '';
    },
  },
  beforeDestroy() {},
};
